import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Drawer,
  IconButton,
  Chip,
  Paper,
  Tabs,
  Tab,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  LinearProgress,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow,
  FileDownload,
  Description,
  ArrowBack,
  Search,
  Settings,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  PauseCircle,
  Stop,
  Assessment,
  Terminal,
  BugReport,
  Code,
  Refresh,
  ExpandMore,
  ChevronRight,
  DarkMode,
  LightMode,
} from '@mui/icons-material';
import axios from 'axios';
import LogoutButton from './LogoutButton';
import { gradients } from '../theme';
import GlobalPayloadConfig from './GlobalPayloadConfig';
import { applyGlobalPayloadToSuites } from '../utils/globalPayload';

const API_BASE = '/test-dashboard';

const testDashboardAPI = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Helper function to properly format payload (handles both string and object formats)
const formatPayload = (payload: any): string => {
  if (!payload) return '{}';
  
  // If payload is already a string, try to parse and re-format it
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If parsing fails, return the original string
      return payload;
    }
  }
  
  // If it's an object, stringify it
  return JSON.stringify(payload, null, 2);
};

/** Check if payload looks malformed (e.g. nested objects stored as "{" string) */
const isPayloadMalformed = (payload: any): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const str = JSON.stringify(payload);
  return /"orderInfo"\s*:\s*"\{"|"dimension"\s*:\s*"\{"/.test(str) || str.includes('": "{');
};

/** Extract top-level JSON object from gherkin text (lines with # prefix; allow leading spaces) */
const extractJsonFromGherkin = (gherkin: string): any => {
  if (!gherkin || typeof gherkin !== 'string') return null;
  const text = gherkin
    .split('\n')
    .map((l) => l.replace(/^\s*#\s*/, '').trim())
    .join('\n');
  const firstBrace = text.indexOf('{');
  if (firstBrace === -1) return null;
  let depth = 0;
  let inString = false;
  let quote: string | null = null;
  let escape = false;
  for (let i = firstBrace; i < text.length; i++) {
    const c = text[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (c === '\\' && inString) {
      escape = true;
      continue;
    }
    if (!inString) {
      if (c === '"' || c === "'") {
        inString = true;
        quote = c;
        continue;
      }
      if (c === '{') {
        depth++;
        continue;
      }
      if (c === '}') {
        depth--;
        if (depth === 0) {
          try {
            const raw = text.slice(firstBrace, i + 1).replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
            return JSON.parse(raw);
          } catch {
            return null;
          }
        }
        continue;
      }
      continue;
    }
    if (c === quote) {
      inString = false;
      quote = null;
    }
  }
  return null;
};

/** Resolve scenario payload for execution: use payload/apiRequest, or extract from gherkin if empty. */
function resolveScenarioPayload(scenario: Scenario): Record<string, unknown> {
  let payload = scenario.payload ?? scenario.apiRequest ?? {};
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = {};
    }
  }
  const isEmpty =
    payload == null ||
    typeof payload !== 'object' ||
    (typeof payload === 'object' && !Array.isArray(payload) && Object.keys(payload).length === 0);
  if (isEmpty && scenario.gherkin) {
    const fromGherkin = extractJsonFromGherkin(scenario.gherkin);
    if (fromGherkin && typeof fromGherkin === 'object' && Object.keys(fromGherkin).length > 0) {
      return fromGherkin as Record<string, unknown>;
    }
  }
  return payload != null && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : {};
}

/** Build suite(s) with resolved payloads for run suite / run all (so backend receives non-empty payloads). */
function suitesWithResolvedPayloads(suites: Suite[]): Suite[] {
  return suites.map((suite) => ({
    ...suite,
    scenarios: (suite.scenarios || []).map((sc) => ({
      ...sc,
      payload: resolveScenarioPayload(sc),
    })),
  }));
}

interface Scenario {
  id: string;
  testCaseId: string;
  title: string;
  gherkin: string;
  apiRequest: any;
  apiResponse: any;
  apiUrl: string;
  apiMethod: string;
  payload: any;
  source: string;
  disabled?: boolean;
  isCommented?: boolean;
  commentReason?: string;
  __overrideGlobal?: boolean;
}

interface Suite {
  id: string;
  name: string;
  scenarios: Scenario[];
  category: string;
}

interface TestResult {
  scenarioId: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  startTime?: string;
  endTime?: string;
  duration?: number;
  error?: string;
  errorLogs?: string;
  stacktrace?: string;
  response?: any;
  testCaseId?: string;
  scenarioTitle?: string;
}

interface LogEntry {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'debug';
  message: string;
  testCaseId?: string;
  scenarioId?: string;
}

type GeneratedOrderIdEntry = {
  testCaseId: string;
  title: string;
  orderId: string;
};

interface TestDashboardProps {
  onLogout: () => void;
}

// Modern Status Card Component
const StatusCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  trend?: string;
  textColor?: string;
  onClick?: () => void;
  isActive?: boolean;
}> = ({ title, value, icon, color, bgColor, trend, textColor, onClick, isActive }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2.5,
      borderRadius: 3,
      background: bgColor,
      border: isActive ? `2px solid ${color}` : `1px solid ${alpha(color, 0.2)}`,
      minWidth: 160,
      flex: 1,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: onClick ? 'pointer' : 'default',
      transform: isActive ? 'translateY(-2px)' : 'none',
      boxShadow: isActive ? `0 8px 24px ${alpha(color, 0.25)}` : 'none',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
        borderColor: alpha(color, 0.4),
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: textColor || 'text.secondary',
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            color: color,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
        {trend && (
          <Typography sx={{ fontSize: '11px', color: textColor || 'text.secondary', mt: 0.5 }}>
            {trend}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: alpha(color, 0.15),
          color: color,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Box>
);

const TestDashboard: React.FC<TestDashboardProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [allSuites, setAllSuites] = useState<Suite[]>([]);
  const [currentSuite, setCurrentSuite] = useState<Suite | null>(null);
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [activeLogTab, setActiveLogTab] = useState<'console' | 'errors' | 'debug'>('console');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableUrls, setAvailableUrls] = useState<Record<string, string>>({});
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('local');
  const [currentBaseUrl, setCurrentBaseUrl] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [jsonPayload, setJsonPayload] = useState<string>('{}');
  const [isRunning, setIsRunning] = useState(false);
  const [consoleHeight, setConsoleHeight] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [globalPayloadOpen, setGlobalPayloadOpen] = useState(false);
  const [confirmRunAllOpen, setConfirmRunAllOpen] = useState(false);
  const [confirmRunSuiteOpen, setConfirmRunSuiteOpen] = useState(false);
  const [sidebarCollapsed] = useState(false);
  const [loadDispatchExpanded, setLoadDispatchExpanded] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const saved = localStorage.getItem('testDashboardTheme');
    return saved ? saved === 'dark' : true; // default to dark mode
  });
  const [statusFilter, setStatusFilter] = useState<'all' | 'passed' | 'failed' | 'running' | 'pending'>('all');
  const [generatedOrderIdSummary, setGeneratedOrderIdSummary] = useState<{
    runLabel: string;
    entries: GeneratedOrderIdEntry[];
    updatedAt: string;
  } | null>(null);

  // Theme colors based on mode
  const theme = useMemo(() => ({
    bg: isDarkMode ? '#0a0e1a' : '#f5f7fa',
    bgSecondary: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    bgTertiary: isDarkMode ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.95)',
    bgCard: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
    bgCardHover: isDarkMode ? 'rgba(124, 58, 237, 0.1)' : 'rgba(124, 58, 237, 0.08)',
    bgInput: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    borderLight: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)',
    textPrimary: isDarkMode ? '#ffffff' : '#1e293b',
    textSecondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
    textMuted: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 41, 59, 0.5)',
    shadow: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.08)',
    consoleBg: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
  }), [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem('testDashboardTheme', newValue ? 'dark' : 'light');
      return newValue;
    });
  };

  const logContentRef = useRef<HTMLDivElement>(null);
  const consoleRef = useRef<HTMLDivElement>(null);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(280);
  const isUserScrolledUp = useRef(false);
  const runTokenRef = useRef<string | null>(null);
  const frontendOrderIdCounter = useRef(0);

  // Calculate test statistics
  const testStats = useMemo(() => {
    const results = Object.values(testResults);
    const total = allSuites.reduce((acc, suite) => acc + (suite.scenarios?.length || 0), 0);
    const passed = results.filter((r) => r.status === 'passed').length;
    const failed = results.filter((r) => r.status === 'failed').length;
    const running = results.filter((r) => r.status === 'running').length;
    const pending = total - passed - failed - running;
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
    return { total, passed, failed, running, pending, passRate };
  }, [testResults, allSuites]);

  // Load API URLs
  useEffect(() => {
    loadApiUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load suites
  useEffect(() => {
    loadSuites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE for real-time logs
  useEffect(() => {
    let es: EventSource | null = null;
    try {
      es = new EventSource(`${API_BASE}/tests/logs`);

      es.addEventListener('init', (e: MessageEvent) => {
        try {
          const payload = JSON.parse((e as any).data || '[]');
          if (Array.isArray(payload)) {
            const newLogs = (payload || []).map((log: any) => ({
              timestamp: log.timestamp || new Date().toISOString(),
              level: log.level || 'info',
              message: log.message || '',
              testCaseId: log.testCaseId,
              scenarioId: log.scenarioId,
            }));
            setLogs(newLogs);
          } else if (payload && typeof payload === 'object') {
            const newLogs = (payload.logs || []).map((log: any) => ({
              timestamp: log.timestamp || new Date().toISOString(),
              level: log.level || 'info',
              message: log.message || '',
              testCaseId: log.testCaseId,
              scenarioId: log.scenarioId,
            }));
            setLogs(newLogs);
            const existingResults = payload.results || [];
            if (existingResults.length > 0) {
              const map: Record<string, TestResult> = {};
              existingResults.forEach((r: any) => {
                map[r.scenarioId] = r;
              });
              setTestResults(map);
            }
          }
        } catch (err) {}
      });

      es.addEventListener('log', (e: MessageEvent) => {
        try {
          const log = JSON.parse((e as any).data || '{}');
          const msg = log.message || '';
          // Force scroll to bottom when a new test case starts executing
          if (msg.includes('Starting test:') || msg.includes('=== Starting Suite:')) {
            isUserScrolledUp.current = false;
          }
          setLogs((prev) => [
            ...prev,
            {
              timestamp: log.timestamp || new Date().toISOString(),
              level: log.level || 'info',
              message: msg,
              testCaseId: log.testCaseId,
              scenarioId: log.scenarioId,
            },
          ]);
        } catch (err) {}
      });

      es.addEventListener('result', (e: MessageEvent) => {
        try {
          const result = JSON.parse((e as any).data || '{}');
          setTestResults((prev) => ({
            ...prev,
            [result.scenarioId]: result,
          }));
        } catch (err) {}
      });
    } catch (err) {}

    return () => {
      if (es) {
        es.close();
      }
    };
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logContentRef.current && !isUserScrolledUp.current) {
      const element = logContentRef.current;
      const threshold = 50;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
      if (isNearBottom) {
        element.scrollTop = element.scrollHeight;
      }
    }
  }, [isRunning]);

  // Auto-scroll logs after DOM update, unless user scrolled up
  useEffect(() => {
    if (!logContentRef.current || isUserScrolledUp.current) return;
    requestAnimationFrame(() => {
      if (logContentRef.current && !isUserScrolledUp.current) {
        logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
      }
    });
  }, [logs]);

  useEffect(() => {
    if (logContentRef.current) {
      setTimeout(() => {
        if (logContentRef.current) {
          logContentRef.current.scrollTop = logContentRef.current.scrollHeight;
          isUserScrolledUp.current = false;
        }
      }, 0);
    }
  }, [activeLogTab]);

  useEffect(() => {
    const element = logContentRef.current;
    if (!element) return;

    const handleScroll = () => {
      const threshold = 50;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
      isUserScrolledUp.current = !isNearBottom;
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, []);

  const loadApiUrls = async () => {
    try {
      const response = await testDashboardAPI.get('/api-urls');
      if (response.data.data?.urls) {
        setAvailableUrls(response.data.data.urls);
        const env = selectedEnvironment || 'local';
        const url = response.data.data.urls[env];
        if (url) {
          setCurrentBaseUrl(url);
          await updateApiBaseUrl(env, url);
        }
      }
    } catch (error: any) {
      addLog('error', `Failed to load API URLs: ${error.message}`);
    }
  };

  const updateApiBaseUrl = async (environment: string, url: string) => {
    try {
      await testDashboardAPI.post('/api-url', { url, environment });
      setCurrentBaseUrl(url);
      addLog('success', `API base URL updated to ${environment}: ${url}`);
    } catch (error: any) {
      addLog('error', `Failed to update API base URL: ${error.message}`);
    }
  };

  const loadSuites = async () => {
    try {
      addLog('info', 'Loading test suites...');
      const response = await testDashboardAPI.get('/suites');
      if (response.data.data?.suites) {
        setAllSuites(response.data.data.suites);
        addLog('success', `Loaded ${response.data.data.suites.length} test suites`);
      }
    } catch (error: any) {
      addLog('error', `Failed to load test suites: ${error.message}`);
    }
  };

  const addLog = (level: LogEntry['level'], message: string) => {
    setLogs((prev) => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        level,
        message,
      },
    ]);
  };

  const markRunningAsAborted = () => {
    const now = new Date().toISOString();
    setTestResults((prev) => {
      const updated: Record<string, TestResult> = { ...prev };
      Object.keys(updated).forEach((k) => {
        if (updated[k]?.status === 'running') {
          updated[k] = {
            ...updated[k],
            status: 'failed',
            error: 'Aborted',
            endTime: now,
          };
        }
      });
      return updated;
    });
  };
  const generateFrontendOrderId = () => {
    frontendOrderIdCounter.current += 1;
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const seq = String(frontendOrderIdCounter.current).padStart(3, '0');
    return `ORD-${date}-${seq}`;
  };

  const hasOrderIdField = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some((item) => hasOrderIdField(item));
    if (typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, 'orderId')) return true;
      return Object.values(value).some((item) => hasOrderIdField(item));
    }
    return false;
  };

  const collectOrderIdValues = (value: any, acc: any[] = []): any[] => {
    if (value === null || value === undefined) return acc;
    if (Array.isArray(value)) {
      value.forEach((item) => collectOrderIdValues(item, acc));
      return acc;
    }
    if (typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, 'orderId')) {
        acc.push((value as any).orderId);
      }
      Object.values(value).forEach((item) => collectOrderIdValues(item, acc));
    }
    return acc;
  };

  const isMissingOrderIdValue = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value !== 'string') return false;
    const normalized = value.trim().toLowerCase();
    return normalized === '' || normalized === 'auto' || normalized === '__auto__' || normalized === '{{orderid}}' || normalized === '<auto>';
  };

  const hasUsableOrderId = (value: any): boolean => {
    if (!value || typeof value !== 'object') return false;
    const orderIdValues = collectOrderIdValues(value);
    if (orderIdValues.length === 0) return false;
    return orderIdValues.some((orderId) => !isMissingOrderIdValue(orderId));
  };

  const fillMissingOrderIds = (value: any, newOrderId: string): any => {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) return value.map((item) => fillMissingOrderIds(item, newOrderId));
    if (typeof value === 'object') {
      const next: any = {};
      Object.keys(value).forEach((key) => {
        if (key === 'orderId') {
          next[key] = isMissingOrderIdValue(value[key]) ? newOrderId : value[key];
        } else {
          next[key] = fillMissingOrderIds(value[key], newOrderId);
        }
      });
      return next;
    }
    return value;
  };

  const isOrderBookingScenario = (scenario: Scenario, payload: any) => {
    const url = (scenario.apiUrl || '').toLowerCase();
    const testCaseId = (scenario.testCaseId || '').toUpperCase();
    return url.includes('/order/booking') || testCaseId.startsWith('LD-BOOK') || hasOrderIdField(payload);
  };

  const ensureOrderIdForScenarioPayload = (scenario: Scenario, payload: any) => {
    if (!isOrderBookingScenario(scenario, payload)) {
      return { payload, generatedOrderId: null as string | null };
    }

    if (hasUsableOrderId(payload)) {
      return { payload, generatedOrderId: null as string | null };
    }

    const generatedOrderId = generateFrontendOrderId();
    const payloadObject = payload && typeof payload === 'object' ? payload : {};
    if (hasOrderIdField(payloadObject)) {
      return {
        payload: fillMissingOrderIds(payloadObject, generatedOrderId),
        generatedOrderId,
      };
    }
    if (Array.isArray(payloadObject)) {
      return { payload: payloadObject, generatedOrderId: null as string | null };
    }
    return {
      payload: { ...payloadObject, orderId: generatedOrderId },
      generatedOrderId,
    };
  };

  const prepareScenarioForExecution = (scenario: Scenario) => {
    const basePayload = scenario.payload ?? scenario.apiRequest ?? {};
    const { payload: normalizedPayload, generatedOrderId } = ensureOrderIdForScenarioPayload(scenario, basePayload);

    return {
      preparedScenario: {
        ...scenario,
        payload: normalizedPayload,
      },
      generatedOrderId,
    };
  };

  const prepareSuiteForExecution = (suite: Suite, selectedScenarioIds?: Set<string>) => {
    const generatedOrderIds: GeneratedOrderIdEntry[] = [];

    const scenarios = suite.scenarios.map((scenario) => {
      if (selectedScenarioIds && !selectedScenarioIds.has(scenario.id)) {
        return scenario;
      }

      if (scenario.disabled || scenario.isCommented) {
        return scenario;
      }

      const { preparedScenario, generatedOrderId } = prepareScenarioForExecution(scenario);
      if (generatedOrderId) {
        generatedOrderIds.push({
          testCaseId: scenario.testCaseId,
          title: scenario.title,
          orderId: generatedOrderId,
        });
      }
      return preparedScenario;
    });

    return {
      preparedSuite: {
        ...suite,
        scenarios,
      },
      generatedOrderIds,
    };
  };

  const logGeneratedOrderIds = (runLabel: string, generatedOrderIds: GeneratedOrderIdEntry[]) => {
    if (generatedOrderIds.length === 0) {
      setGeneratedOrderIdSummary(null);
      return;
    }

    setGeneratedOrderIdSummary({
      runLabel,
      entries: [...generatedOrderIds],
      updatedAt: new Date().toISOString(),
    });

    addLog('info', `Auto-generated orderId for ${generatedOrderIds.length} scenario(s) with missing orderId`);
    generatedOrderIds.forEach((entry) => {
      addLog('debug', `orderId ${entry.orderId} -> ${entry.testCaseId}: ${entry.title}`);
    });
  };

  const handleSuiteSelect = (suite: Suite) => {
    if (!suite.scenarios || suite.scenarios.length === 0) {
      addLog('info', `Suite "${suite.name}" has no scenarios`);
      return;
    }
    setCurrentSuite(suite);
    setCurrentScenario(null);
    setDrawerOpen(false);
  };

  const handleScenarioClick = (scenario: Scenario) => {
    setCurrentScenario(scenario);
    setDrawerOpen(true);
    
    // Use payload/apiRequest; if empty, malformed, or not an object, try gherkin (description source)
    let payload = scenario.payload ?? scenario.apiRequest ?? {};
    const isEmpty = typeof payload !== 'object' || Object.keys(payload).length === 0;
    const malformed = isPayloadMalformed(payload);
    if ((isEmpty || malformed) && scenario.gherkin) {
      const fromGherkin = extractJsonFromGherkin(scenario.gherkin);
      if (fromGherkin && typeof fromGherkin === 'object' && Object.keys(fromGherkin).length > 0) {
        payload = fromGherkin;
      }
    }
    setJsonPayload(formatPayload(payload));
  };

  const handleUrlChange = async (environment: string) => {
    setSelectedEnvironment(environment);
    const url = availableUrls[environment];
    if (url) {
      await updateApiBaseUrl(environment, url);
    }
  };

  const runAllTests = async () => {
    const token = `${Date.now()}`;
    runTokenRef.current = token;
    setIsRunning(true);
    const runningResults: Record<string, TestResult> = {};
    allSuites.forEach((suite) => {
      suite.scenarios.forEach((scenario) => {
        runningResults[scenario.id] = {
          scenarioId: scenario.id,
          status: 'running',
          startTime: new Date().toISOString(),
        };
      });
    });
    setTestResults(runningResults);

    try {
      addLog('info', 'Starting all tests...');
      const suitesToSend = suitesWithResolvedPayloads(allSuites);
      const generatedOrderIds: GeneratedOrderIdEntry[] = [];
      const preparedSuites = suitesToSend.map((suite) => {
        const { preparedSuite, generatedOrderIds: suiteGeneratedOrderIds } = prepareSuiteForExecution(suite);
        generatedOrderIds.push(...suiteGeneratedOrderIds);
        return preparedSuite;
      });
      logGeneratedOrderIds('All Tests', generatedOrderIds);
      const response = await testDashboardAPI.post('/tests/run-all', { suites: preparedSuites });

      // Ignore response if a stop was requested (token changed)
      if (runTokenRef.current !== token) {
        addLog('info', 'RunAll response ignored due to abort/overlap');
        return;
      }

      if (response.data.data?.results) {
        const results: Record<string, TestResult> = { ...runningResults };
        response.data.data.results.forEach((result: TestResult) => {
          results[result.scenarioId] = result;
          if (result.status === 'passed') {
            addLog('success', `✓ ${result.testCaseId}: PASSED (${result.duration}ms)`);
          } else {
            addLog('error', `✗ ${result.testCaseId}: FAILED - ${result.error || 'Unknown error'}`);
          }
        });
        // Mark any scenario still 'running' as failed so nothing stays stuck
        Object.keys(results).forEach((id) => {
          if (results[id].status === 'running') {
            results[id] = { ...results[id], status: 'failed', error: 'No result received' };
          }
        });
        setTestResults(results);

        const summary = response.data.data.summary || {};
        addLog('success', `All tests completed. ${summary.passed || 0} passed, ${summary.failed || 0} failed`);
      }
    } catch (error: any) {
      addLog('error', `Failed to run tests: ${error.message}`);
      // On error, clear running state for all scenarios we marked running
      setTestResults((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          if (next[id].status === 'running') {
            next[id] = { ...next[id], status: 'failed', error: error.message || 'Run failed' };
          }
        });
        return next;
      });
    } finally {
      // Only clear running flag if this run hasn't been superseded/aborted
      if (runTokenRef.current === token) {
        setIsRunning(false);
        runTokenRef.current = null;
      }
    }
  };

  const runCurrentSuite = async () => {
    if (!currentSuite) return;
    const token = `${Date.now()}`;
    runTokenRef.current = token;
    setIsRunning(true);

    const runningResults: Record<string, TestResult> = { ...testResults };
    currentSuite.scenarios.forEach((scenario) => {
      runningResults[scenario.id] = {
        scenarioId: scenario.id,
        status: 'running',
        startTime: new Date().toISOString(),
      };
    });
    setTestResults(runningResults);

    try {
      addLog('info', `Running suite: ${currentSuite.name}...`);
      const suiteToSend = suitesWithResolvedPayloads([currentSuite])[0];
      const { preparedSuite, generatedOrderIds } = prepareSuiteForExecution(suiteToSend);
      logGeneratedOrderIds(`Suite: ${currentSuite.name}`, generatedOrderIds);
      const response = await testDashboardAPI.post(`/tests/suites/${currentSuite.id}/run`, { suite: preparedSuite });

      if (runTokenRef.current !== token) {
        addLog('info', 'RunSuite response ignored due to abort/overlap');
        return;
      }

      if (response.data.data?.results) {
        const results: Record<string, TestResult> = { ...testResults };
        response.data.data.results.forEach((result: TestResult) => {
          results[result.scenarioId] = result;
          if (result.status === 'passed') {
            addLog('success', `✓ ${result.testCaseId}: PASSED (${result.duration}ms)`);
          } else {
            addLog('error', `✗ ${result.testCaseId}: FAILED - ${result.error || 'Unknown error'}`);
          }
        });
        // Mark any scenario we set to running that has no final result (avoid stuck pending)
        currentSuite.scenarios.forEach((scenario) => {
          if (results[scenario.id]?.status === 'running') {
            results[scenario.id] = {
              ...results[scenario.id],
              status: 'failed',
              error: 'No result received',
            };
          }
        });
        setTestResults(results);

        const summary = response.data.data.summary || {};
        addLog('success', `Suite completed. ${summary.passed || 0} passed, ${summary.failed || 0} failed`);
      }
    } catch (error: any) {
      addLog('error', `Failed to run suite: ${error.message}`);
      // On error, clear running state for this suite's scenarios
      setTestResults((prev) => {
        const next = { ...prev };
        currentSuite.scenarios.forEach((scenario) => {
          if (next[scenario.id]?.status === 'running') {
            next[scenario.id] = {
              ...next[scenario.id],
              status: 'failed',
              error: error.message || 'Suite run failed',
            };
          }
        });
        return next;
      });
    } finally {
      if (runTokenRef.current === token) {
        setIsRunning(false);
        runTokenRef.current = null;
      }
    }
  };

  const runScenario = async () => {
    if (!currentSuite || !currentScenario) return;

    let payload: any;
    try {
      payload = JSON.parse(jsonPayload);
    } catch (error) {
      addLog('error', 'Invalid JSON in payload editor');
      return;
    }

    const { payload: normalizedPayload, generatedOrderId } = ensureOrderIdForScenarioPayload(currentScenario, payload);
    payload = normalizedPayload;
    logGeneratedOrderIds(
      `Scenario: ${currentScenario.testCaseId || currentScenario.title}`,
      generatedOrderId
        ? [{ testCaseId: currentScenario.testCaseId, title: currentScenario.title, orderId: generatedOrderId }]
        : []
    );
    if (generatedOrderId) {
      setJsonPayload(JSON.stringify(payload, null, 2));
      addLog('info', `Auto-generated orderId on frontend: ${generatedOrderId}`);
    }

    // Save payload first
    try {
      await testDashboardAPI.put(
        `/suites/${currentSuite.id}/scenarios/${currentScenario.id}/payload`,
        { payload }
      );
    } catch (error: any) {
      addLog('error', `Failed to save payload: ${error.message}`);
    }

    setTestResults((prev) => ({
      ...prev,
      [currentScenario.id]: {
        scenarioId: currentScenario.id,
        status: 'running',
        startTime: new Date().toISOString(),
      },
    }));

    try {
      addLog('info', `Running scenario: ${currentScenario.title}...`);
      const response = await testDashboardAPI.post(
        `/tests/suites/${currentSuite.id}/scenarios/${currentScenario.id}/run`,
        { payload }
      );

      if (response.data.data?.result) {
        const result = response.data.data.result;
        setTestResults((prev) => ({
          ...prev,
          [result.scenarioId]: result,
        }));

        if (result.status === 'passed') {
          addLog('success', `✓ Scenario PASSED: ${currentScenario.title} (${result.duration}ms)`);
        } else {
          addLog('error', `✗ Scenario FAILED: ${currentScenario.title} - ${result.error || 'Unknown error'}`);
        }
      }
    } catch (error: any) {
      setTestResults((prev) => ({
        ...prev,
        [currentScenario.id]: {
          scenarioId: currentScenario.id,
          status: 'failed',
          error: error.message,
          endTime: new Date().toISOString(),
        },
      }));
      addLog('error', `Failed to run scenario: ${error.message}`);
    }
  };

  const savePayload = async () => {
    if (!currentSuite || !currentScenario) return;

    try {
      const payload = JSON.parse(jsonPayload);
      await testDashboardAPI.put(
        `/suites/${currentSuite.id}/scenarios/${currentScenario.id}/payload`,
        { payload }
      );
      addLog('success', 'Payload saved successfully');
    } catch (error: any) {
      addLog('error', `Failed to save payload: ${error.message}`);
    }
  };

  const resetPayload = () => {
    if (!currentScenario) return;
    let originalPayload = currentScenario.apiRequest ?? currentScenario.payload ?? {};
    const isEmpty = typeof originalPayload !== 'object' || Object.keys(originalPayload).length === 0;
    const malformed = isPayloadMalformed(originalPayload);
    if ((isEmpty || malformed) && currentScenario.gherkin) {
      const fromGherkin = extractJsonFromGherkin(currentScenario.gherkin);
      if (fromGherkin && typeof fromGherkin === 'object' && Object.keys(fromGherkin).length > 0) {
        originalPayload = fromGherkin;
      }
    }
    setJsonPayload(formatPayload(originalPayload));
    addLog('info', 'Payload reset to default');
  };

  const exportCSV = async () => {
    try {
      const response = await testDashboardAPI.get('/tests/export-csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test-results-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      addLog('success', 'CSV exported successfully');
    } catch (error: any) {
      addLog('error', `Failed to export CSV: ${error.message}`);
    }
  };

  const filteredSuites = allSuites.filter(
    (suite) =>
      suite.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      suite.scenarios.some((s) => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Load Dispatch: one entry that opens a drawer with sub-categories
  const isLoadDispatchGroup = useMemo(
    () =>
      filteredSuites.length > 1 &&
      filteredSuites.every((s) => s.name?.includes('Test Cases') || s.name?.includes('Edge Case')),
    [filteredSuites]
  );
  const loadDispatchSubSuites = isLoadDispatchGroup ? filteredSuites : [];

  const filteredLogs = logs.filter((log) => {
    if (activeLogTab === 'console') return true;
    if (activeLogTab === 'errors') return log.level === 'error';
    if (activeLogTab === 'debug') return log.level === 'debug';
    return true;
  });

  const getStatusBadge = (scenario: Scenario) => {
    const isDisabled = scenario.disabled === true || scenario.isCommented === true;

    if (isDisabled) {
      return (
        <Chip
          label="Disabled"
          size="small"
          sx={{
            backgroundColor: alpha('#ef4444', 0.1),
            color: '#ef4444',
            border: `1px solid ${alpha('#ef4444', 0.3)}`,
            fontSize: '10px',
            fontWeight: 600,
            height: '22px',
          }}
        />
      );
    }

    const result = testResults[scenario.id];
    const status = result?.status || 'pending';

    const statusConfig: Record<string, { color: string; bg: string; icon: string }> = {
      passed: { color: '#10b981', bg: alpha('#10b981', 0.1), icon: '✓' },
      failed: { color: '#ef4444', bg: alpha('#ef4444', 0.1), icon: '✗' },
      running: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1), icon: '◉' },
      pending: { color: '#6b7280', bg: alpha('#6b7280', 0.1), icon: '○' },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={`${config.icon} ${status.charAt(0).toUpperCase() + status.slice(1)}`}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          border: `1px solid ${alpha(config.color, 0.3)}`,
          fontSize: '10px',
          fontWeight: 600,
          height: '22px',
        }}
      />
    );
  };

  const getFullUrl = (apiUrl: string) => {
    if (!apiUrl) return 'Not specified';
    if (apiUrl.startsWith('http')) return apiUrl;
    const base = currentBaseUrl.endsWith('/') ? currentBaseUrl.slice(0, -1) : currentBaseUrl;
    const path = apiUrl.startsWith('/') ? apiUrl : `/${apiUrl}`;
    return `${base}${path}`;
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = consoleHeight;
    e.preventDefault();
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const diff = resizeStartY.current - e.clientY;
      const newHeight = Math.max(150, Math.min(500, resizeStartHeight.current + diff));
      setConsoleHeight(newHeight);
      localStorage.setItem('consoleHeight', newHeight.toString());
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing]);

  useEffect(() => {
    const savedHeight = localStorage.getItem('consoleHeight');
    if (savedHeight) {
      setConsoleHeight(parseInt(savedHeight, 10));
    }
  }, []);

  // Get suite stats for sidebar
  const getSuiteStats = (suite: Suite) => {
    const passed = suite.scenarios.filter((s) => testResults[s.id]?.status === 'passed').length;
    const failed = suite.scenarios.filter((s) => testResults[s.id]?.status === 'failed').length;
    const total = suite.scenarios.length;
    return { passed, failed, total };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        bgcolor: theme.bg,
        color: theme.textPrimary,
        position: 'relative',
        transition: 'background-color 0.3s ease, color 0.3s ease',
      }}
    >
      {/* Top Navigation Bar */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          px: 3,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: theme.bgSecondary,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${theme.border}`,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Back to Main Dashboard">
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                background: theme.bgInput,
                color: theme.textSecondary,
                border: `1px solid ${theme.borderLight}`,
                '&:hover': {
                  background: 'rgba(124, 58, 237, 0.15)',
                  borderColor: 'rgba(124, 58, 237, 0.3)',
                  color: 'primary.main',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.textPrimary,
                letterSpacing: '-0.5px',
              }}
            >
              Test Automation Hub
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme.textSecondary }}>
              QA Dashboard • {selectedEnvironment.charAt(0).toUpperCase() + selectedEnvironment.slice(1)} Environment
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedEnvironment}
              onChange={(e) => handleUrlChange(e.target.value)}
              sx={{
                bgcolor: theme.bgInput,
                border: `1px solid ${theme.borderLight}`,
                borderRadius: 2,
                color: theme.textPrimary,
                '& .MuiSelect-select': {
                  py: 1,
                  fontSize: '13px',
                },
                '& .MuiSelect-icon': {
                  color: theme.textSecondary,
                },
                '&:hover': {
                  borderColor: 'rgba(124, 58, 237, 0.3)',
                },
              }}
            >
              {Object.entries(availableUrls).map(([key, url]) => (
                <MenuItem key={key} value={key}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Global Payload Config">
            <IconButton
              onClick={() => setGlobalPayloadOpen(true)}
              sx={{
                background: theme.bgInput,
                color: theme.textSecondary,
                '&:hover': { background: 'rgba(124, 58, 237, 0.15)', color: 'primary.main' },
              }}
            >
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            <IconButton
              onClick={toggleTheme}
              sx={{
                background: theme.bgInput,
                color: theme.textSecondary,
                border: `1px solid ${theme.borderLight}`,
                '&:hover': { 
                  background: isDarkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(99, 102, 241, 0.15)', 
                  color: isDarkMode ? '#fbbf24' : '#6366f1',
                  borderColor: isDarkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(99, 102, 241, 0.3)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {isDarkMode ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </IconButton>
          </Tooltip>

          <LogoutButton onLogout={onLogout} position="relative" top={0} right={0} isDarkMode={isDarkMode} />
        </Box>
      </Box>

      {/* Status Overview Bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: theme.bgTertiary,
          borderBottom: `1px solid ${theme.border}`,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
          <StatusCard
            title="Total Tests"
            value={testStats.total}
            icon={<Assessment fontSize="small" />}
            color="#8b5cf6"
            bgColor={isDarkMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.12)'}
            textColor={theme.textSecondary}
            onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
            isActive={statusFilter === 'all'}
          />
          <StatusCard
            title="Passed"
            value={testStats.passed}
            icon={<CheckCircle fontSize="small" />}
            color="#10b981"
            bgColor={isDarkMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.12)'}
            trend={testStats.total > 0 ? `${testStats.passRate}% pass rate` : undefined}
            textColor={theme.textSecondary}
            onClick={() => setStatusFilter(statusFilter === 'passed' ? 'all' : 'passed')}
            isActive={statusFilter === 'passed'}
          />
          <StatusCard
            title="Failed"
            value={testStats.failed}
            icon={<ErrorIcon fontSize="small" />}
            color="#ef4444"
            bgColor={isDarkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.12)'}
            textColor={theme.textSecondary}
            onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')}
            isActive={statusFilter === 'failed'}
          />
          <StatusCard
            title="Running"
            value={testStats.running}
            icon={<Schedule fontSize="small" />}
            color="#f59e0b"
            bgColor={isDarkMode ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.12)'}
            textColor={theme.textSecondary}
            onClick={() => setStatusFilter(statusFilter === 'running' ? 'all' : 'running')}
            isActive={statusFilter === 'running'}
          />
          <StatusCard
            title="Pending"
            value={testStats.pending}
            icon={<Schedule fontSize="small" />}
            color="#6b7280"
            bgColor={isDarkMode ? 'rgba(107, 114, 128, 0.08)' : 'rgba(107, 114, 128, 0.12)'}
            textColor={theme.textSecondary}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
            isActive={statusFilter === 'pending'}
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setConfirmRunAllOpen(true)}
              disabled={isRunning}
              startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              sx={{
                background: gradients.primary,
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  background: gradients.primaryDark,
                },
                '&:disabled': {
                  background: 'rgba(124, 58, 237, 0.3)',
                },
              }}
            >
              {isRunning ? 'Running...' : 'Run All Tests'}
            </Button>

            {isRunning && (
              <Tooltip title="Stop All Tests">
                <IconButton
                  onClick={async () => {
                    try {
                      await testDashboardAPI.post('/tests/stop');
                      markRunningAsAborted();
                      setIsRunning(false);
                      addLog('success', 'Tests stopped');
                    } catch (err: any) {
                      addLog('error', `Failed to stop: ${err.message}`);
                    }
                  }}
                  sx={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    '&:hover': {
                      background: 'rgba(239, 68, 68, 0.25)',
                    },
                  }}
                >
                  <Stop fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Export Results">
              <IconButton
                onClick={exportCSV}
                sx={{
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  color: theme.textSecondary,
                  border: `1px solid ${theme.borderLight}`,
                  '&:hover': { background: 'rgba(124, 58, 237, 0.15)', color: '#8b5cf6' },
                }}
              >
                <FileDownload fontSize="small" />
              </IconButton>
            </Tooltip>
            {generatedOrderIdSummary && (
              <Tooltip
                arrow
                placement="bottom"
                title={
                  <Box sx={{ py: 0.25 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, mb: 0.5 }}>
                      {generatedOrderIdSummary.runLabel}
                    </Typography>
                    {generatedOrderIdSummary.entries.slice(0, 6).map((entry) => (
                      <Typography
                        key={`${entry.orderId}-${entry.testCaseId}`}
                        sx={{ fontSize: '11px', lineHeight: 1.5, fontFamily: "'Monaco', 'Menlo', monospace" }}
                      >
                        {entry.testCaseId}: {entry.orderId}
                      </Typography>
                    ))}
                    {generatedOrderIdSummary.entries.length > 6 && (
                      <Typography sx={{ fontSize: '11px', mt: 0.5, opacity: 0.85 }}>
                        +{generatedOrderIdSummary.entries.length - 6} more
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Chip
                  size="small"
                  label={`Order IDs: ${generatedOrderIdSummary.entries.length}`}
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.16)',
                    color: '#34d399',
                    border: '1px solid rgba(16, 185, 129, 0.5)',
                    fontSize: '11px',
                    fontWeight: 700,
                    height: '24px',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Progress indicator when running */}
        {isRunning && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="indeterminate"
              sx={{
                height: 3,
                borderRadius: 2,
                bgcolor: 'rgba(124, 58, 237, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: gradients.primary,
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Sidebar - Suite List */}
        <Box
          sx={{
            width: sidebarCollapsed ? 60 : 300,
            minWidth: sidebarCollapsed ? 60 : 280,
            bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.8)' : 'rgba(255, 255, 255, 0.95)',
            borderRight: `1px solid ${theme.border}`,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease, background-color 0.3s ease',
            flexShrink: 0,
          }}
        >
          {!sidebarCollapsed && (
            <>
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.border}` }}>
                <TextField
                  fullWidth
                  placeholder="Search suites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: theme.textSecondary, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: theme.bgInput,
                      borderRadius: 2,
                      fontSize: '13px',
                      color: theme.textPrimary,
                      '& input': {
                        color: theme.textPrimary,
                        '&::placeholder': {
                          color: theme.textMuted,
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  py: 1,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                  },
                }}
              >
                {isLoadDispatchGroup ? (
                  <>
                    <Box
                      onClick={() => setLoadDispatchExpanded((prev) => !prev)}
                      sx={{
                        mx: 1,
                        mb: 0.5,
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: loadDispatchExpanded ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                        borderLeft: loadDispatchExpanded ? '3px solid #8b5cf6' : '3px solid transparent',
                        '&:hover': {
                          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)',
                          borderLeftColor: 'rgba(124, 58, 237, 0.4)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '13px',
                            color: theme.textPrimary,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'visible',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            pr: 1,
                          }}
                        >
                          Load Dispatch Suite
                        </Typography>
                        <ExpandMore
                          sx={{
                            fontSize: 20,
                            color: theme.textSecondary,
                            transform: loadDispatchExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '11px', color: theme.textSecondary }}>
                        {loadDispatchSubSuites.length} sub-categories • {loadDispatchExpanded ? 'collapse' : 'expand'}
                      </Typography>
                    </Box>
                    {loadDispatchExpanded &&
                      loadDispatchSubSuites.map((suite) => {
                        const stats = getSuiteStats(suite);
                        const isSelected = currentSuite?.id === suite.id;

                        return (
                          <Box
                            key={suite.id}
                            onClick={() => handleSuiteSelect(suite)}
                            sx={{
                              mx: 1,
                              ml: 2,
                              mb: 0.5,
                              p: 1.25,
                              pl: 2,
                              borderRadius: 2,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              bgcolor: isSelected ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
                              borderLeft: isSelected ? '3px solid #8b5cf6' : '3px solid transparent',
                              '&:hover': {
                                bgcolor: isSelected ? 'rgba(124, 58, 237, 0.2)' : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)'),
                              },
                              opacity: suite.scenarios.length === 0 ? 0.5 : 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: '12px',
                                color: isSelected ? (isDarkMode ? 'white' : '#1e293b') : theme.textPrimary,
                                flex: 1,
                                minWidth: 0,
                                overflow: 'visible',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                                pr: 1,
                              }}
                            >
                              {suite.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography sx={{ fontSize: '10px', color: theme.textSecondary }}>
                                {stats.total} tests
                              </Typography>
                              {stats.passed > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10b981' }} />
                                  <Typography sx={{ fontSize: '10px', color: '#10b981' }}>{stats.passed}</Typography>
                                </Box>
                              )}
                              {stats.failed > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ef4444' }} />
                                  <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>{stats.failed}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                  </>
                ) : (
                  filteredSuites.map((suite) => {
                    const stats = getSuiteStats(suite);
                    const isSelected = currentSuite?.id === suite.id;

                    return (
                      <Box
                        key={suite.id}
                        onClick={() => handleSuiteSelect(suite)}
                        sx={{
                          mx: 1,
                          mb: 0.5,
                          p: 1.5,
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          bgcolor: isSelected ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
                          borderLeft: isSelected ? '3px solid #8b5cf6' : '3px solid transparent',
                          '&:hover': {
                            bgcolor: isSelected ? 'rgba(124, 58, 237, 0.2)' : (isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.04)'),
                          },
                          opacity: suite.scenarios.length === 0 ? 0.5 : 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography
                            sx={{
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: '13px',
                              color: isSelected ? (isDarkMode ? 'white' : '#1e293b') : theme.textPrimary,
                              flex: 1,
                              minWidth: 0,
                              overflow: 'visible',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              pr: 1,
                            }}
                          >
                            {suite.name}
                          </Typography>
                          <ChevronRight
                            sx={{
                              fontSize: 16,
                              color: theme.textSecondary,
                              transform: isSelected ? 'rotate(90deg)' : 'none',
                              transition: 'transform 0.2s',
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '11px', color: theme.textSecondary }}>
                            {stats.total} tests
                          </Typography>
                          {stats.passed > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                              <Typography sx={{ fontSize: '10px', color: '#10b981' }}>{stats.passed}</Typography>
                            </Box>
                          )}
                          {stats.failed > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                              <Typography sx={{ fontSize: '10px', color: '#ef4444' }}>{stats.failed}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Global Payload Modal */}
        <GlobalPayloadConfig
          open={globalPayloadOpen}
          onClose={() => setGlobalPayloadOpen(false)}
          isDarkMode={isDarkMode}
          onApply={(globalPayload, opts) => {
            try {
              // honor opts.addMissing passed from the modal (defaults to false)
              const updated = applyGlobalPayloadToSuites(allSuites, globalPayload, opts || { addMissing: false });
              setAllSuites(updated);
              
              // Also update currentSuite to reflect changes immediately
              if (currentSuite) {
                const updatedSuite = updated.find((s) => s.id === currentSuite.id);
                if (updatedSuite) {
                  setCurrentSuite(updatedSuite);
                }
              }
              
              // Update current scenario editor if open
              if (currentScenario) {
                const found = updated
                  .flatMap((s) => s.scenarios || [])
                  .find((sc: any) => sc.id === currentScenario.id);
                if (found) {
                  const payload = found.payload || found.apiRequest || {};
                  setJsonPayload(formatPayload(payload));
                  setCurrentScenario(found);
                }
              }
              
              addLog('success', `Global payload applied to ${Object.keys(globalPayload).length} key(s)`);
            } catch (e) {
              addLog('error', `Failed to apply global payload: ${(e as Error).message}`);
            }
          }}
          suites={allSuites}
        />

        {/* Confirmation Dialogs */}
        <Dialog
          open={confirmRunAllOpen}
          onClose={() => setConfirmRunAllOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 400,
              bgcolor: theme.bgSecondary,
              color: theme.textPrimary,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, color: theme.textPrimary }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PlayArrow sx={{ color: 'primary.main' }} />
              <Typography component="span" sx={{ fontWeight: 600, overflow: 'visible', wordBreak: 'break-word' }}>Run All Tests</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ overflow: 'visible' }}>
            <Typography sx={{ color: theme.textSecondary, overflow: 'visible', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
              This will execute all {testStats.total} tests across {allSuites.length} suites. Continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmRunAllOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setConfirmRunAllOpen(false);
                await runAllTests();
              }}
              sx={{ background: gradients.primary }}
            >
              Run All Tests
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmRunSuiteOpen}
          onClose={() => setConfirmRunSuiteOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: 400,
              bgcolor: theme.bgSecondary,
              color: theme.textPrimary,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, color: theme.textPrimary }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PlayArrow sx={{ color: 'primary.main' }} />
              <Typography component="span" sx={{ fontWeight: 600, overflow: 'visible', wordBreak: 'break-word' }}>Run Suite</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ overflow: 'visible' }}>
            <Typography sx={{ color: theme.textSecondary, overflow: 'visible', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
              Execute all {currentSuite?.scenarios.length || 0} scenarios in &quot;{currentSuite?.name}&quot;?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmRunSuiteOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setConfirmRunSuiteOpen(false);
                await runCurrentSuite();
              }}
              sx={{ background: gradients.primary }}
            >
              Run Suite
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Suite Header */}
          {currentSuite && (
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: `1px solid ${theme.border}`,
                background: isDarkMode ? 'rgba(15, 23, 42, 0.4)' : 'rgba(248, 250, 252, 0.8)',
                transition: 'background-color 0.3s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.textPrimary,
                      mb: 0.5,
                    }}
                  >
                    {currentSuite.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
                      {currentSuite.scenarios.length} test scenarios • {currentSuite.category || 'General'}
                    </Typography>
                    {statusFilter !== 'all' && (
                      <Chip
                        label={`Showing: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} (${
                          currentSuite.scenarios.filter((s) => {
                            const result = testResults[s.id];
                            return (result?.status || 'pending') === statusFilter;
                          }).length
                        })`}
                        size="small"
                        onDelete={() => setStatusFilter('all')}
                        sx={{
                          height: 22,
                          fontSize: '11px',
                          fontWeight: 600,
                          bgcolor: statusFilter === 'passed' ? 'rgba(16, 185, 129, 0.15)' :
                                   statusFilter === 'failed' ? 'rgba(239, 68, 68, 0.15)' :
                                   statusFilter === 'running' ? 'rgba(245, 158, 11, 0.15)' :
                                   'rgba(107, 114, 128, 0.15)',
                          color: statusFilter === 'passed' ? '#10b981' :
                                 statusFilter === 'failed' ? '#ef4444' :
                                 statusFilter === 'running' ? '#f59e0b' :
                                 '#6b7280',
                          '& .MuiChip-deleteIcon': {
                            color: 'inherit',
                            '&:hover': {
                              color: 'inherit',
                              opacity: 0.7,
                            },
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => setConfirmRunSuiteOpen(true)}
                    disabled={isRunning}
                    startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
                    size="small"
                    sx={{
                      background: gradients.primary,
                      fontWeight: 600,
                    }}
                  >
                    Run Suite
                  </Button>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={loadSuites}
                      size="small"
                      sx={{
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                        color: theme.textSecondary,
                        '&:hover': { background: 'rgba(124, 58, 237, 0.15)', color: '#8b5cf6' },
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}

          {/* Scenarios Grid */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 3,
              bgcolor: isDarkMode ? 'transparent' : 'rgba(248, 250, 252, 0.5)',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
                borderRadius: '6px',
              },
            }}
          >
            {!currentSuite ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: theme.textSecondary,
                }}
              >
                <Assessment sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: theme.textMuted }} />
                <Typography variant="h6" sx={{ mb: 1, color: theme.textPrimary }}>
                  Select a Test Suite
                </Typography>
                <Typography sx={{ fontSize: '14px', textAlign: 'center', maxWidth: 400, color: theme.textSecondary }}>
                  Choose a test suite from the sidebar to view and run test scenarios
                </Typography>
              </Box>
            ) : currentSuite.scenarios.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: theme.textSecondary,
                }}
              >
                <BugReport sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: theme.textMuted }} />
                <Typography variant="h6" sx={{ mb: 1, color: theme.textPrimary }}>
                  No Scenarios Found
                </Typography>
                <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>This suite does not contain any test scenarios</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: 2,
                }}
              >
                {currentSuite.scenarios.filter((scenario) => {
                    if (statusFilter === 'all') return true;
                    const result = testResults[scenario.id];
                    const status = result?.status || 'pending';
                    return status === statusFilter;
                  }).length === 0 ? (
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 6,
                        color: theme.textSecondary,
                      }}
                    >
                      <Assessment sx={{ fontSize: 48, mb: 2, opacity: 0.3, color: theme.textMuted }} />
                      <Typography variant="h6" sx={{ mb: 1, color: theme.textPrimary }}>
                        No {statusFilter} tests found
                      </Typography>
                      <Typography sx={{ fontSize: '14px', mb: 2, color: theme.textSecondary }}>
                        There are no test cases with "{statusFilter}" status in this suite
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setStatusFilter('all')}
                        sx={{ textTransform: 'none' }}
                      >
                        Show all tests
                      </Button>
                    </Box>
                  ) : currentSuite.scenarios.filter((scenario) => {
                    if (statusFilter === 'all') return true;
                    const result = testResults[scenario.id];
                    const status = result?.status || 'pending';
                    return status === statusFilter;
                  }).map((scenario) => {
                  const isDisabled = scenario.disabled === true || scenario.isCommented === true;
                  const result = testResults[scenario.id];
                  const status = result?.status || 'pending';
                  const lastRun = result?.endTime
                    ? new Date(result.endTime).toLocaleTimeString()
                    : null;

                  const statusColors: Record<string, string> = {
                    passed: '#10b981',
                    failed: '#ef4444',
                    running: '#f59e0b',
                    pending: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  };

                  return (
                    <Paper
                      key={scenario.id}
                      onClick={() => handleScenarioClick(scenario)}
                      sx={{
                        p: 2,
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.8)',
                        border: `1px solid ${isDisabled ? 'rgba(239, 68, 68, 0.2)' : theme.border}`,
                        borderRadius: 2.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: isDisabled ? '#ef4444' : statusColors[status],
                          transition: 'all 0.2s',
                        },
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.04)',
                          borderColor: isDisabled ? 'rgba(239, 68, 68, 0.3)' : 'rgba(124, 58, 237, 0.3)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        },
                        opacity: isDisabled ? 0.7 : 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '13px',
                            color: theme.textPrimary,
                            flex: 1,
                            pr: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {scenario.testCaseId && (
                            <Box
                              component="span"
                              sx={{
                                color: '#8b5cf6',
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                mr: 1,
                              }}
                            >
                              {scenario.testCaseId}
                            </Box>
                          )}
                          {scenario.title || 'Untitled Scenario'}
                        </Typography>
                        {getStatusBadge(scenario)}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {scenario.apiMethod && (
                            <Chip
                              label={scenario.apiMethod}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '10px',
                                fontWeight: 600,
                                bgcolor: 'rgba(59, 130, 246, 0.15)',
                                color: '#3b82f6',
                                border: 'none',
                              }}
                            />
                          )}
                          {scenario.__overrideGlobal && (
                            <Chip
                              label="Override"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '10px',
                                fontWeight: 600,
                                bgcolor: 'rgba(245, 158, 11, 0.15)',
                                color: '#f59e0b',
                                border: 'none',
                              }}
                            />
                          )}
                        </Box>

                        <Typography sx={{ fontSize: '11px', color: theme.textSecondary }}>
                          {lastRun ? `Last: ${lastRun}` : 'Not run'}
                          {result?.duration && ` • ${result.duration}ms`}
                        </Typography>
                      </Box>

                      {result?.error && !isDisabled && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            color: '#ef4444',
                            mt: 1,
                            p: 1,
                            bgcolor: 'rgba(239, 68, 68, 0.08)',
                            borderRadius: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {result.error}
                        </Typography>
                      )}

                      {isDisabled && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            color: theme.textSecondary,
                            mt: 1,
                            fontStyle: 'italic',
                          }}
                        >
                          {scenario.commentReason || 'Commented out in source'}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Console Panel */}
          <Box
            ref={consoleRef}
            sx={{
              height: `${consoleHeight}px`,
              minHeight: 150,
              bgcolor: isDarkMode ? 'rgba(10, 14, 26, 0.95)' : 'rgba(248, 250, 252, 0.98)',
              borderTop: `1px solid ${theme.border}`,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              flexShrink: 0,
              transition: 'background-color 0.3s ease',
            }}
          >
            {/* Resize Handle */}
            <Box
              onMouseDown={handleResizeStart}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 8,
                cursor: 'ns-resize',
                zIndex: 10,
                background: 'transparent',
                transition: 'background 0.2s',
                '&:hover': {
                  background: 'rgba(124, 58, 237, 0.3)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            />

            {/* Console Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <Tabs
                value={activeLogTab}
                onChange={(_, newValue) => setActiveLogTab(newValue)}
                sx={{
                  minHeight: 36,
                  '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0,
                    px: 2,
                    fontSize: '12px',
                    fontWeight: 500,
                    color: theme.textSecondary,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: '#8b5cf6',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: 2,
                    borderRadius: 1,
                    bgcolor: '#8b5cf6',
                  },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Terminal sx={{ fontSize: 14 }} />
                      Console
                    </Box>
                  }
                  value="console"
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <ErrorIcon sx={{ fontSize: 14 }} />
                      Errors
                      {logs.filter((l) => l.level === 'error').length > 0 && (
                        <Chip
                          label={logs.filter((l) => l.level === 'error').length}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '10px',
                            bgcolor: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            ml: 0.5,
                          }}
                        />
                      )}
                    </Box>
                  }
                  value="errors"
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Code sx={{ fontSize: 14 }} />
                      Debug
                    </Box>
                  }
                  value="debug"
                />
              </Tabs>

              <Button
                size="small"
                onClick={() => {
                  setLogs([]);
                  addLog('info', 'Cleared');
                }}
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: '11px',
                  color: theme.textSecondary,
                  '&:hover': { color: theme.textPrimary, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' },
                }}
              >
                Clear
              </Button>
            </Box>

            {/* Console Content */}
            <Box
              ref={logContentRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2,
                py: 1,
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Monaco', monospace",
                fontSize: '12px',
                lineHeight: 1.6,
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                },
              }}
            >
              {filteredLogs.length === 0 ? (
                <Box sx={{ py: 2, color: theme.textSecondary, fontSize: '12px', fontStyle: 'italic' }}>
                  Console ready. Run a test to see output.
                </Box>
              ) : (
                filteredLogs.map((log, index) => (
                  <Box
                    key={index}
                    sx={{
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      '&:hover': {
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        color: theme.textMuted,
                        fontSize: '10px',
                        flexShrink: 0,
                        fontFamily: 'inherit',
                      }}
                    >
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color:
                          log.level === 'error'
                            ? '#ef4444'
                            : log.level === 'success'
                            ? '#10b981'
                            : log.level === 'debug'
                            ? (isDarkMode ? '#6b7280' : '#94a3b8')
                            : theme.textPrimary,
                        fontWeight: log.level === 'error' || log.level === 'success' ? 500 : 400,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        fontFamily: 'inherit',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'visible',
                      }}
                    >
                      {log.message}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Scenario Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 520,
            bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderLeft: `1px solid ${theme.border}`,
          },
        }}
      >
        {currentScenario && (
          <>
            {/* Drawer Header */}
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${theme.border}`,
                position: 'relative',
              }}
            >
              {(currentScenario.disabled || currentScenario.isCommented) && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                    borderRadius: 2,
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <PauseCircle sx={{ color: '#ef4444', fontSize: 20 }} />
                  <Box>
                    <Typography sx={{ color: '#ef4444', fontWeight: 600, fontSize: '12px' }}>
                      Test Disabled
                    </Typography>
                    <Typography sx={{ color: theme.textSecondary, fontSize: '11px' }}>
                      {currentScenario.commentReason || 'Commented out in source'}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ pr: 5 }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#8b5cf6',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.5,
                  }}
                >
                  {currentScenario.testCaseId}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.textPrimary, mb: 1 }}>
                  {currentScenario.title}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={currentScenario.apiMethod || 'POST'}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '10px',
                      fontWeight: 600,
                      bgcolor: 'rgba(59, 130, 246, 0.15)',
                      color: '#3b82f6',
                    }}
                  />
                  {getStatusBadge(currentScenario)}
                </Box>
              </Box>

              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  color: theme.textSecondary,
                  '&:hover': {
                    bgcolor: 'rgba(239, 68, 68, 0.15)',
                    color: '#ef4444',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* API URL Section */}
            <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${theme.border}` }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: theme.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                }}
              >
                API Endpoint
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 1.5,
                  border: `1px solid ${theme.border}`,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '12px',
                  color: theme.textPrimary,
                  wordBreak: 'break-all',
                }}
              >
                {getFullUrl(currentScenario.apiUrl)}
              </Box>
            </Box>

            {/* Payload Editor */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setDescriptionModalOpen(true)}
                startIcon={<Description />}
                sx={{ mb: 2 }}
              >
                View Full Description
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.textPrimary }}>
                  Request Payload
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      if (!currentScenario) return;
                      const updated = allSuites.map((s) => ({
                        ...s,
                        scenarios: (s.scenarios || []).map((sc: any) =>
                          sc.id === currentScenario.id ? { ...sc, __overrideGlobal: !sc.__overrideGlobal } : sc
                        ),
                      }));
                      setAllSuites(updated);
                      const found = updated.flatMap((s) => s.scenarios || []).find((sc: any) => sc.id === currentScenario.id);
                      if (found) {
                        setCurrentScenario(found);
                      }
                    }}
                    sx={{
                      fontSize: '10px',
                      color: currentScenario.__overrideGlobal ? '#f59e0b' : theme.textSecondary,
                    }}
                  >
                    {currentScenario.__overrideGlobal ? 'Override Active' : 'Enable Override'}
                  </Button>
                </Box>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={14}
                value={jsonPayload}
                onChange={(e) => setJsonPayload(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    fontSize: '12px',
                    lineHeight: 1.5,
                    color: theme.textPrimary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.border,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.borderLight,
                    },
                  },
                }}
              />

              {/* API Response (last run) */}
              {currentScenario && testResults[currentScenario.id]?.response && (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: theme.textPrimary, mb: 1.5 }}>
                    API Response
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: isDarkMode ? 'rgba(0, 0, 0, 0.35)' : 'rgba(0, 0, 0, 0.06)',
                      border: `1px solid ${theme.border}`,
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: '11px',
                      lineHeight: 1.5,
                      color: theme.textPrimary,
                      overflowX: 'auto',
                      overflowY: 'auto',
                      maxHeight: 220,
                    }}
                  >
                    <Typography component="div" sx={{ fontSize: '11px', color: theme.textSecondary, mb: 1 }}>
                      {testResults[currentScenario.id].response.status} {testResults[currentScenario.id].response.statusText}
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '11px',
                        color: theme.textPrimary,
                      }}
                    >
                      {typeof testResults[currentScenario.id].response.data === 'object'
                        ? JSON.stringify(testResults[currentScenario.id].response.data, null, 2)
                        : String(testResults[currentScenario.id].response.data ?? '')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Drawer Actions */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${theme.border}`,
                display: 'flex',
                gap: 1.5,
                bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.8)' : 'rgba(248, 250, 252, 0.95)',
              }}
            >
              <Button variant="outlined" onClick={resetPayload} sx={{ flex: 1 }}>
                Reset
              </Button>
              <Button variant="outlined" onClick={savePayload} sx={{ flex: 1 }}>
                Save
              </Button>
              {currentScenario?.disabled || currentScenario?.isCommented ? (
                <Button 
                  variant="outlined" 
                  disabled 
                  sx={{ 
                    flex: 1.5,
                    '&.Mui-disabled': {
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  Not Runnable
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={runScenario}
                  disabled={isRunning}
                  startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
                  sx={{
                    flex: 1.5,
                    background: gradients.primary,
                  }}
                >
                  Run Test
                </Button>
              )}
            </Box>
          </>
        )}
      </Drawer>

      {/* Description Modal */}
      <Modal
        open={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            borderRadius: 3,
            p: 0,
            maxWidth: 800,
            maxHeight: '80vh',
            width: '90%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
          }}
        >
          <Box
            sx={{
              p: 2.5,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.textPrimary }}>
              Test Scenario Details
            </Typography>
            <IconButton onClick={() => setDescriptionModalOpen(false)} sx={{ color: theme.textSecondary }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {currentScenario && (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 3,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '12px',
                lineHeight: 1.8,
                whiteSpace: 'pre-wrap',
                color: theme.textPrimary,
              }}
            >
              {`Test Case: ${currentScenario.testCaseId}\nTitle: ${currentScenario.title}\n\n${
                currentScenario.gherkin ? `=== Gherkin Scenario ===\n${currentScenario.gherkin}\n\n` : ''
              }=== API Details ===\nMethod: ${currentScenario.apiMethod || 'POST'}\nURL: ${currentScenario.apiUrl || 'Not specified'}\n\n${
                currentScenario.apiRequest || currentScenario.payload
                  ? `=== Request Payload ===\n${formatPayload(currentScenario.payload || currentScenario.apiRequest)}\n\n`
                  : ''
              }${
                currentScenario.apiResponse
                  ? `=== Expected Response ===\n${formatPayload(currentScenario.apiResponse)}\n\n`
                  : ''
              }${currentScenario.source ? `\nSource: ${currentScenario.source}` : ''}`}
            </Box>
          )}
        </Paper>
      </Modal>
    </Box>
  );
};

export default TestDashboard;
